/**
 * Target-Value Strategy for Best-Fit Processing
 *
 * Implements the efficiency-first approach for bond projects with target constraints.
 * This strategy prioritizes efficiency (principal vs interest ratio) over raw principal
 * maximization, and uses bidirectional search to find the most efficient solution.
 *
 * Optimization Goal: Maximize efficiency while achieving target amounts
 * Search Method: Bidirectional (efficiency-driven direction selection)
 * Stopping Criteria: When efficiency improvement stops
 * Viability: Must achieve target amounts + standard viability
 */

import { getShapePosition } from '~/engine/millage/shape-progression';
import { BondCategorizationEngine } from '~/engine/processing/shared/bond-categorization';
import { logger } from '~/services/logging';
import type { Candidate, Configuration } from '~/types';
import { TargetType } from '~/types/candidate';
import { BaseBestFitStrategy } from './base-strategy';
import { ComparisonEngine } from './comparison-engine';
import { getBestCandidate, getFirstFitCandidates } from './shared-utilities';
import {
  ProjectType,
  type SearchContext,
  type SearchDirection,
  type SearchResult,
  type SearchStoppingReason,
} from './strategy-types';

/**
 * Target-Value Strategy Implementation
 *
 * This strategy focuses on achieving target principal amounts with maximum efficiency,
 * making it ideal for projects with specific bond amount requirements where the goal
 * is to achieve those targets with the best principal-to-interest ratio.
 */

export class TargetValueStrategy extends BaseBestFitStrategy {
  readonly strategyType = ProjectType.TARGET_VALUE;
  readonly strategyName = 'Target-Value Strategy';
  readonly description = 'Achieve targets with maximum efficiency';

  /**
   * Target-value viability requires both standard viability AND target achievement.
   */
  isViable(candidate: Candidate, context?: SearchContext): boolean {
    // Must meet standard viability first
    if (!candidate.results.isViable) {
      return false;
    }

    // Must achieve all target requirements (need context for configuration)
    if (!context) {
      return false; // Cannot check targets without context
    }

    return this.meetsTargetRequirements(candidate, context.configuration);
  }

  /**
   * Selects the most efficient first-fit candidate that achieves target requirements.
   * This prioritizes efficiency over raw principal amount.
   */
  selectBestFirstFit(candidates: Candidate[], configuration: Configuration): Candidate | null {
    const firstFitCandidates = getFirstFitCandidates(candidates);

    // Filter to only those that meet target requirements
    // Note: isViable needs context, but we can check basic viability here
    const targetAchievingCandidates = firstFitCandidates.filter((c) => {
      if (!c.results.isViable) return false;
      return this.meetsTargetRequirements(c, configuration);
    });

    if (targetAchievingCandidates.length === 0) {
      logger.L3.info('🎯 Target-Value: No candidates achieve target requirements');
      return null;
    }

    // Use comparison engine to get the most efficient candidate
    const strategy = BondCategorizationEngine.determineOrderingStrategy(
      targetAchievingCandidates,
      configuration
    );
    const sortFunction = ComparisonEngine.getSortFunctionForStrategy(strategy);
    const bestCandidate = getBestCandidate(targetAchievingCandidates, sortFunction);

    if (bestCandidate) {
      const efficiency = this.getPrimaryMetric(bestCandidate);
      logger.L3.info(
        `🎯 Target-Value: Selected ${bestCandidate.name} (Efficiency: ${(efficiency * 100).toFixed(2)}%)`
      );
    }

    return bestCandidate;
  }

  /**
   * Executes bidirectional search optimizing for efficiency.
   * Tries both +1 and -1 directions, continues with the more efficient direction.
   */
  async executeSearch(startingCandidate: Candidate, context: SearchContext): Promise<SearchResult> {
    const startTime = Date.now();
    // Note: baseElements, configuration, projectData available in context if needed
    const maxCandidates = 50;

    this.logStrategyStart(1, startingCandidate);

    const startingShapeId = startingCandidate.millageInformation.millageShape.shapeId;
    const baselineEfficiency = this.getPrimaryMetric(startingCandidate);
    const allCandidates: Candidate[] = [];
    const searchPath: string[] = [startingShapeId];

    // Check if we're already at the optimal efficiency position
    // For target projects, log-decel-26 (first shape) is the most efficient
    const shapePosition = getShapePosition(startingShapeId);
    if (shapePosition.isFirst) {
      logger.L3.info(
        `🎯 Already at most efficient shape (${startingShapeId}) - skipping best-fit search`
      );
      const processingTime = Date.now() - startTime;
      const searchDetails = this.createSearchDetails(
        startingCandidate,
        0,
        null,
        baselineEfficiency,
        0
      );

      const result = this.createSearchResult(
        startingCandidate,
        allCandidates,
        searchPath,
        'already_at_optimal_efficiency' as SearchStoppingReason,
        searchDetails,
        processingTime
      );

      this.logStrategyComplete(result);
      return result;
    }

    // Step 1: Try both directions (+1 and -1 from starting position)
    const initialForward = this.tryDirection(context, startingShapeId, 'forward');
    const initialBackward = this.tryDirection(context, startingShapeId, 'backward');

    if (initialForward) allCandidates.push(initialForward);
    if (initialBackward) allCandidates.push(initialBackward);

    // Step 2: Determine which direction to explore based on efficiency
    const forwardEfficiency = initialForward ? this.getPrimaryMetric(initialForward) : -1;
    const backwardEfficiency = initialBackward ? this.getPrimaryMetric(initialBackward) : -1;

    let bestCandidateSoFar = startingCandidate;
    let chosenDirection: SearchDirection | null = null;
    let bestEfficiencySoFar = baselineEfficiency;

    // Choose direction based on which shows better efficiency
    // For targeted bonds, prefer the direction with better efficiency even if it's not an improvement
    // This ensures we explore to find the optimal efficiency point
    if (initialForward && initialBackward) {
      // Both directions viable - choose the better one
      if (forwardEfficiency >= backwardEfficiency) {
        chosenDirection = 'forward';
        bestCandidateSoFar = initialForward;
        bestEfficiencySoFar = forwardEfficiency;
        searchPath.push(this.getNextShape(startingShapeId, 'forward')!);
      } else {
        chosenDirection = 'backward';
        bestCandidateSoFar = initialBackward;
        bestEfficiencySoFar = backwardEfficiency;
        searchPath.push(this.getNextShape(startingShapeId, 'backward')!);
      }
    } else if (initialForward) {
      // Only forward is viable
      chosenDirection = 'forward';
      bestCandidateSoFar = initialForward;
      bestEfficiencySoFar = forwardEfficiency;
      searchPath.push(this.getNextShape(startingShapeId, 'forward')!);
    } else if (initialBackward) {
      // Only backward is viable
      chosenDirection = 'backward';
      bestCandidateSoFar = initialBackward;
      bestEfficiencySoFar = backwardEfficiency;
      searchPath.push(this.getNextShape(startingShapeId, 'backward')!);
    }

    // Step 3: Continue exploring in the chosen direction
    let stoppingReason: SearchStoppingReason = 'no_improvement_found';

    if (chosenDirection) {
      const explorationResult = await this.exploreDirection(
        context,
        bestCandidateSoFar.millageInformation.millageShape.shapeId,
        chosenDirection,
        bestEfficiencySoFar,
        maxCandidates - allCandidates.length
      );

      allCandidates.push(...explorationResult.candidates);
      searchPath.push(...explorationResult.shapeIds);

      if (explorationResult.bestCandidate) {
        bestCandidateSoFar = explorationResult.bestCandidate;
        bestEfficiencySoFar = this.getPrimaryMetric(bestCandidateSoFar);
      }

      stoppingReason = explorationResult.stoppingReason;
    }

    // Mark all candidates as processed
    allCandidates.forEach((candidate) => {
      candidate.isProcessed = true;
      candidate.createdInPhase = 'best-fit';
    });

    // Add the starting candidate to allCandidates if not already there
    if (!allCandidates.find((c) => c.id === startingCandidate.id)) {
      allCandidates.unshift(startingCandidate);
    }

    // Re-select the best candidate from ALL candidates generated
    // This ensures we pick the actual best, not just the last one tracked
    const actualBestCandidate = this.selectBestFromAll(allCandidates, context);

    const processingTime = Date.now() - startTime;
    const searchDetails = this.createSearchDetails(
      startingCandidate,
      allCandidates.length,
      chosenDirection,
      this.getPrimaryMetric(actualBestCandidate),
      this.getPrimaryMetric(actualBestCandidate) - baselineEfficiency
    );

    const result = this.createSearchResult(
      actualBestCandidate,
      allCandidates,
      searchPath,
      stoppingReason,
      searchDetails,
      processingTime
    );

    this.logStrategyComplete(result);
    return result;
  }

  /**
   * Selects the best candidate from all candidates generated during the search.
   * This ensures we pick the actual best, not just the last one tracked during search.
   */
  private selectBestFromAll(candidates: Candidate[], context: SearchContext): Candidate {
    const strategy = BondCategorizationEngine.determineOrderingStrategy(
      candidates,
      context.configuration
    );
    const sortFunction = ComparisonEngine.getSortFunctionForStrategy(strategy);
    const bestCandidate = getBestCandidate(candidates, sortFunction);

    if (!bestCandidate) {
      throw new Error('No best candidate found from all candidates');
    }

    logger.L3.info(
      `🎯 Re-selected best from all ${candidates.length} candidates: ${bestCandidate.name}`
    );
    return bestCandidate;
  }

  /**
   * Gets efficiency as the primary metric for target-value strategy.
   */
  getPrimaryMetric(candidate: Candidate): number {
    return candidate.results.efficiency || 0;
  }

  /**
   * Returns human-readable name for the primary metric.
   */
  getPrimaryMetricName(): string {
    return 'Efficiency';
  }

  /**
   * Validates that candidates have target information and meet requirements.
   */
  validateCandidates(candidates: Candidate[]): string | null {
    const baseValidation = super.validateCandidates(candidates);
    if (baseValidation) return baseValidation;

    // Must have target information
    // Note: This validation needs configuration - updating to accept it
    // For now, skip this check or get configuration from context
    // TODO: Update validateCandidates signature to accept configuration

    // Must have at least one candidate that achieves targets
    // Note: validateCandidates doesn't have context, so we can't check targets here
    // This is a limitation - targets will be checked in selectBestFirstFit instead
    const firstFitCandidates = getFirstFitCandidates(candidates);
    const viableCandidates = firstFitCandidates.filter((c) => c.results.isViable);
    const targetAchievingCandidates = viableCandidates; // Will be filtered properly in selectBestFirstFit

    if (targetAchievingCandidates.length === 0) {
      return 'No candidates achieve the required target amounts';
    }

    return null;
  }

  /**
   * Checks if a candidate meets all target requirements.
   */
  private meetsTargetRequirements(candidate: Candidate, configuration: Configuration): boolean {
    if (!configuration.bondSeries || configuration.bondSeries.length === 0) return false;

    // Check each bond series with target information
    for (const bond of configuration.bondSeries) {
      if (!bond.targetInformation) continue;

      const { principalTargetAmount, targetType } = bond.targetInformation;

      // Find the actual principal amount for this bond from results
      const bondResult = candidate.results.bondSeriesResults?.find(
        (result) => result.bondSeriesId === bond.id
      );

      if (!bondResult) continue;

      const actualPrincipal = bondResult.totalPrincipal;

      // Check target achievement based on target type
      if (targetType === TargetType.DOES_NOT_EXCEED) {
        // Must not exceed the target (can be less)
        if (actualPrincipal > principalTargetAmount) {
          return false;
        }
        // Should achieve a reasonable portion of the target (at least 80%)
        if (actualPrincipal < principalTargetAmount * 0.8) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Tries a single step in the given direction and returns the candidate if viable.
   */
  private tryDirection(
    context: SearchContext,
    currentShapeId: string,
    direction: SearchDirection
  ): Candidate | null {
    const nextShapeId = this.getNextShape(currentShapeId, direction);

    if (!nextShapeId) {
      return null;
    }

    const candidate = this.createCandidateFromShape(context, nextShapeId);

    // Only return if viable according to target-value criteria
    return candidate && this.isViable(candidate, context) ? candidate : null;
  }

  /**
   * Explores in a direction until efficiency stops improving.
   */
  private async exploreDirection(
    context: SearchContext,
    startingShapeId: string,
    direction: SearchDirection,
    baselineEfficiency: number,
    maxCandidates: number
  ): Promise<{
    candidates: Candidate[];
    shapeIds: string[];
    bestCandidate: Candidate | null;
    stoppingReason: SearchStoppingReason;
  }> {
    const candidates: Candidate[] = [];
    const shapeIds: string[] = [];
    let currentShapeId = startingShapeId;
    let bestEfficiencySoFar = baselineEfficiency;
    let bestCandidateSoFar: Candidate | null = null;
    let candidatesGenerated = 0;

    logger.L3.info(
      `🎯 Exploring ${direction} from ${startingShapeId} (baseline: ${(baselineEfficiency * 100).toFixed(2)}%)`
    );

    while (candidatesGenerated < maxCandidates) {
      const nextShapeId = this.getNextShape(currentShapeId, direction);

      if (!nextShapeId) {
        logger.L3.debug(`🎯 No more shapes in ${direction} direction`);
        return {
          candidates,
          shapeIds,
          bestCandidate: bestCandidateSoFar,
          stoppingReason: 'no_more_shapes',
        };
      }

      const candidate = this.createCandidateFromShape(context, nextShapeId);

      if (!candidate || !this.isViable(candidate, context)) {
        logger.L3.debug(`🎯 Stopping ${direction} exploration: candidate not viable`);
        return {
          candidates,
          shapeIds,
          bestCandidate: bestCandidateSoFar,
          stoppingReason: 'candidate_not_viable',
        };
      }

      candidates.push(candidate);
      shapeIds.push(nextShapeId);
      candidatesGenerated++;

      const currentEfficiency = this.getPrimaryMetric(candidate);
      const improvement = currentEfficiency - bestEfficiencySoFar;

      this.logSearchProgress(
        candidate.name,
        currentEfficiency,
        this.getPrimaryMetricName(),
        improvement
      );

      // For target value strategy, continue as long as there's ANY improvement
      // We want to find the most efficient solution that meets our targets
      // Stop only when efficiency gets worse or stays exactly the same
      if (improvement > 0) {
        bestEfficiencySoFar = currentEfficiency;
        bestCandidateSoFar = candidate;
        currentShapeId = nextShapeId;
      } else if (improvement === 0) {
        // If efficiency is exactly the same, still track the candidate but keep searching
        // This could mean we're at a plateau and might find improvements further along
        currentShapeId = nextShapeId;
        logger.L3.info(`🎯 Same efficiency at ${candidate.name}, continuing search`);
      } else {
        // Efficiency got worse - this is our stopping point
        logger.L3.debug(
          `🎯 Stopping ${direction} exploration: efficiency decreased (${(improvement * 100).toFixed(4)}%)`
        );
        return {
          candidates,
          shapeIds,
          bestCandidate: bestCandidateSoFar,
          stoppingReason: 'efficiency_stopped_improving',
        };
      }
    }

    return {
      candidates,
      shapeIds,
      bestCandidate: bestCandidateSoFar,
      stoppingReason: 'max_candidates_reached',
    };
  }
}
